import React from 'react';
import cl from 'classnames';

import { IconsEnum } from '../../../../../assets/icons/types';
import {
  ID,
  I18nText,
  FetchItemsFilterItems,
  FetchItemsSort,
  FetchItemsSortableField,
  FetchItemsSortItems,
  ClassName,
  GADataType
} from '../../../../../types';
import { ProjectNanoID } from '../../../../projects/projectsTypes';
import { CompactIndexTableHeaderSortFilterCellFilterField } from './CompactIndexTableHeaderSortFilterCell.types';

import { useCompactIndexTableHeaderSortFilterCell } from './hooks/useCompactIndexTableHeaderSortFilterCell';

import { CompactIndexTableHeaderSortFilterCellPopover } from './components/CompactIndexTableHeaderSortFilterCellPopover';

import { Translate } from '../../../../../helpers/Translate';
import { Icon } from '../../../../../helpers/Icon';
import { RefButtonHelper } from '../../../../../helpers/buttons/RefButtonHelper';

interface CompactIndexTableHeaderSortFilterCellProps<FiltersType> {
  viewId: ID;
  filterFields: CompactIndexTableHeaderSortFilterCellFilterField<FiltersType>[];
  currentFilters?: FiltersType;
  currentSort?: FetchItemsSort;
  i18nText?: I18nText;
  onFilter: FetchItemsFilterItems<Partial<FiltersType>>;
  onSort?: FetchItemsSortItems;
  sortableField?: FetchItemsSortableField;
  ascField?: string;
  descField?: string;
  className?: ClassName;
  projectNanoId?: ProjectNanoID;
}

function CompactIndexTableHeaderSortFilterCell<
  FiltersType extends Record<string, any>
>({
  ascField,
  className,
  currentFilters,
  currentSort,
  dataGa,
  descField,
  filterFields,
  i18nText,
  onFilter,
  onSort,
  projectNanoId,
  sortableField,
  viewId
}: CompactIndexTableHeaderSortFilterCellProps<FiltersType> & GADataType) {
  const withSort = Boolean(
    ((ascField && descField) || sortableField) && onSort
  );

  const {
    ascSort,
    descSort,
    isSortedAsc,
    isSortedDesc,
    sortIcon,
    isFiltered,
    localFiltersState,
    setLocalFiltersState,
    isPopoverOpen,
    referencePopoverElement,
    referencePopoverCallbackRef,
    togglePopover,
    closePopover,
    handleApply,
    handleClear
  } = useCompactIndexTableHeaderSortFilterCell({
    viewId,
    filterFields,
    currentFilters,
    currentSort,
    onFilter,
    sortableField,
    ascField,
    descField
  });

  const withoutContent = !i18nText;

  return (
    <th
      scope="col"
      className={
        className ||
        'relative hover:bg-gray-100 dark:hover:bg-gray-800 pl-3 pr-1 py-2 dark:border-gray-700 border-gray-200 border-b'
      }
    >
      <div className="flex items-center justify-center">
        <RefButtonHelper
          className={cl(
            'group w-full rounded focus:ring-0 focus:ring-offset-0',
            withoutContent ? 'h-4' : null
          )}
          onClick={togglePopover}
          ref={referencePopoverCallbackRef}
        >
          <div
            className="absolute inset-0 hover:shadow dark:hover:glow z-5"
            aria-hidden="true"
          />
          <div
            className="absolute inset-0 border-2 border-current opacity-0 group-focus:opacity-100 rounded-md"
            aria-hidden="true"
          />

          <div className="flex gap-1 items-center text-gray-500 justify-start">
            {i18nText ? (
              <span className="text-xs font-medium uppercase tracking-wider whitespace-nowrap">
                <Translate id={i18nText} />
              </span>
            ) : null}

            <div className="flex">
              {isSortedAsc || isSortedDesc ? (
                <Icon className="w-4 h-4" icon={sortIcon} />
              ) : null}
              <Icon
                className={cl(
                  'w-4 h-4',
                  isFiltered
                    ? 'text-blue-500'
                    : 'text-gray-400 dark:text-gray-600'
                )}
                icon={IconsEnum.FILTER_SOLID}
              />
            </div>
          </div>
        </RefButtonHelper>

        {isPopoverOpen ? (
          <CompactIndexTableHeaderSortFilterCellPopover
            ascSort={ascSort}
            closePopover={closePopover}
            currentSort={currentSort}
            dataGa={dataGa}
            descSort={descSort}
            filterFields={filterFields}
            localFiltersState={localFiltersState}
            onApply={handleApply}
            onClear={handleClear}
            onSort={onSort}
            projectNanoId={projectNanoId}
            referencePopoverElement={referencePopoverElement}
            setLocalFiltersState={setLocalFiltersState}
            viewId={viewId}
            withSort={withSort}
          />
        ) : null}
      </div>
    </th>
  );
}

export default CompactIndexTableHeaderSortFilterCell;
